import React, { useState, useEffect } from 'react';
import { 
    CheckCircle, 
    RefreshCw, 
    Power, 
    Server, 
    User, 
    Store, 
    FileText, 
    Tag, 
    Trash2, 
    Save, 
    Users, 
    Package 
} from 'lucide-react';
import './CommonStyles.css';
import './BusinessSettings.css';
import apiService from '../../services/api';
import { quickbooksApi } from '../../services/quickbooksApi';

const TABS = [
    { id: 'business', label: 'Business' },
    { id: 'tax', label: 'Tax' },
    { id: 'product', label: 'Product' },
    { id: 'contact', label: 'Contact' },
    { id: 'sale', label: 'Sale' },
    { id: 'pos', label: 'POS' },
    { id: 'purchases', label: 'Purchases' },
    { id: 'payment', label: 'Payment' },
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'system', label: 'System' },
    { id: 'prefixes', label: 'Prefixes' },
    { id: 'email-settings', label: 'Email Settings' },
    { id: 'sms-settings', label: 'SMS Settings' },
    { id: 'reward-point-settings', label: 'Reward Point Settings' },
    { id: 'modules', label: 'Modules' },
    { id: 'custom-labels', label: 'Custom Labels' },
    { id: 'quickbooks', label: 'QuickBooks' }
];

const BusinessSettings = () => {
    const [activeTab, setActiveTab] = useState('business');
    const [units, setUnits] = useState([]);

    // QuickBooks integration states
    const [isConnected, setIsConnected] = useState(false);
    const [qbLoading, setQbLoading] = useState(false);
    const [qbConnectionData, setQbConnectionData] = useState(null);
    const [qbAccounts, setQbAccounts] = useState([]);
    const [qbAccountsLoading, setQbAccountsLoading] = useState(false);
    const [qbIncomeAccountId, setQbIncomeAccountId] = useState('');
    const [qbExpenseAccountId, setQbExpenseAccountId] = useState('');
    const [qbAssetAccountId, setQbAssetAccountId] = useState('');
    const [qbDepositAccountId, setQbDepositAccountId] = useState('');
    const [qbAutoSync, setQbAutoSync] = useState(false);
    const [qbApiTestResult, setQbApiTestResult] = useState(null);
    const [qbSyncStatus, setQbSyncStatus] = useState({ loading: false, message: '', errors: [] });
    const [qbSavingSettings, setQbSavingSettings] = useState(false);

    const fetchQbConnection = async () => {
        setQbLoading(true);
        try {
            const res = await quickbooksApi.testConnection();
            if (res.success) {
                setIsConnected(true);
                setQbConnectionData(res.data);
            } else {
                setIsConnected(false);
                setQbConnectionData(null);
            }
        } catch (e) {
            setIsConnected(false);
            setQbConnectionData(null);
        } finally {
            setQbLoading(false);
        }
    };

    const fetchQbAccounts = async () => {
        setQbAccountsLoading(true);
        try {
            const res = await quickbooksApi.getAccounts();
            const list = res?.data?.QueryResponse?.Account || [];
            setQbAccounts(list);
        } catch (e) {
            setQbAccounts([]);
        } finally {
            setQbAccountsLoading(false);
        }
    };

    const handleTestConnection = async () => {
        setQbLoading(true);
        setQbApiTestResult(null);
        try {
            const res = await quickbooksApi.testConnection();
            if (res.success) {
                setIsConnected(true);
                setQbConnectionData(res.data);
                setQbApiTestResult({ type: 'success', label: 'Connection', data: res.data });
            } else {
                setQbApiTestResult({ type: 'error', label: 'Connection', message: res.message });
            }
        } catch (e) {
            setQbApiTestResult({ type: 'error', label: 'Connection', message: e.message });
        } finally {
            setQbLoading(false);
        }
    };

    const handleRefreshStatus = async () => {
        await fetchQbConnection();
        if (isConnected) await fetchQbAccounts();
    };

    const handleConnectQb = async () => {
        setQbLoading(true);
        try {
            const res = await quickbooksApi.getAuthorizationUrl();
            if (res.success && res.data?.authorization_url) {
                window.location.href = res.data.authorization_url;
            }
        } catch (e) {
            setQbApiTestResult({ type: 'error', label: 'Connect', message: e.message });
        } finally {
            setQbLoading(false);
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Are you sure you want to disconnect QuickBooks?')) return;
        setQbLoading(true);
        try {
            await quickbooksApi.disconnect();
            setIsConnected(false);
            setQbConnectionData(null);
            setQbAccounts([]);
            setQbApiTestResult({ type: 'success', label: 'Disconnect', message: 'Disconnected successfully.' });
        } catch (e) {
            setQbApiTestResult({ type: 'error', label: 'Disconnect', message: e.message });
        } finally {
            setQbLoading(false);
        }
    };

    const handleSaveMappings = async () => {
        setQbSavingSettings(true);
        try {
            const res = await quickbooksApi.updateSettings({
                income_account_id:  qbIncomeAccountId,
                expense_account_id: qbExpenseAccountId,
                asset_account_id:   qbAssetAccountId,
                deposit_account_id: qbDepositAccountId,
                auto_sync:          qbAutoSync,
            });
            setQbApiTestResult({ type: res.success ? 'success' : 'error', label: 'Settings', message: res.message });
        } catch (e) {
            setQbApiTestResult({ type: 'error', label: 'Settings', message: e.message });
        } finally {
            setQbSavingSettings(false);
        }
    };

    const handleQuickApiTest = async (label, apiFn) => {
        setQbApiTestResult({ type: 'loading', label });
        try {
            const res = await apiFn();
            const data = res?.data?.QueryResponse || res?.data || res;
            setQbApiTestResult({ type: 'success', label, data });
        } catch (e) {
            setQbApiTestResult({ type: 'error', label, message: e.message });
        }
    };

    const handleManualSync = async (type) => {
        setQbSyncStatus({ loading: true, message: '', errors: [] });
        try {
            let res;
            if (type === 'Customers') res = await quickbooksApi.syncAllCustomers();
            else if (type === 'Products') res = await quickbooksApi.syncAllProducts();
            else if (type === 'Invoices') res = await quickbooksApi.syncAllInvoices();
            setQbSyncStatus({ loading: false, message: res?.message || 'Done.', errors: res?.errors || [] });
        } catch (e) {
            setQbSyncStatus({ loading: false, message: e.message, errors: [] });
        }
    };
    const [isLoadingUnits, setIsLoadingUnits] = useState(false);
    const [taxRates, setTaxRates] = useState([]);
    const [isLoadingTaxRates, setIsLoadingTaxRates] = useState(false);

    // UI states
    const [isPaymentMethodDropdownOpen, setIsPaymentMethodDropdownOpen] = useState(false);
    const [selectedPaymentMethods, setSelectedPaymentMethods] = useState(['Cash', 'Cheque']);

    // SMS Settings dynamic states
    const [smsHeaders, setSmsHeaders] = useState([{ id: Date.now(), key: '', value: '' }]);
    const [smsParams, setSmsParams] = useState([{ id: Date.now() + 1, key: '', value: '' }]);

    const addSmsHeader = () => setSmsHeaders([...smsHeaders, { id: Date.now(), key: '', value: '' }]);
    const removeSmsHeader = (id) => setSmsHeaders(smsHeaders.filter(h => h.id !== id));

    const addSmsParam = () => setSmsParams([...smsParams, { id: Date.now(), key: '', value: '' }]);
    const removeSmsParam = (id) => setSmsParams(smsParams.filter(p => p.id !== id));

    // Custom Labels states
    const createInitialFields = (count, extra = {}) => Array.from({ length: count }, (_, i) => ({ id: Date.now() + i, label: '', ...extra }));

    const [payLabels, setPayLabels] = useState(createInitialFields(7));
    const [contactFields, setContactFields] = useState(createInitialFields(10));
    const [productFields, setProductFields] = useState(Array.from({ length: 20 }, (_, i) => ({ id: Date.now() + 100 + i, label: '', type: 'Text' })));
    const [locationFields, setLocationFields] = useState(createInitialFields(4));
    const [userFields, setUserFields] = useState(createInitialFields(4));
    const [purchaseFields, setPurchaseFields] = useState(createInitialFields(4, { isRequired: false }));
    const [purchaseShipFields, setPurchaseShipFields] = useState(createInitialFields(5, { isRequired: false }));
    const [sellFields, setSellFields] = useState(createInitialFields(4, { isRequired: false }));
    const [sellShipFields, setSellShipFields] = useState(createInitialFields(5, { isRequired: false, isDefault: false }));
    const [serviceFields, setServiceFields] = useState(createInitialFields(6));

    const addField = (setter, extra = {}) => setter(prev => [...prev, { id: Date.now(), label: '', ...extra }]);
    const removeField = (setter, id) => setter(prev => prev.filter(f => f.id !== id));

    const [expandedSections, setExpandedSections] = useState({});
    const toggleSection = (section) => setExpandedSections(p => ({ ...p, [section]: !p[section] }));

    useEffect(() => {
        const fetchUnits = async () => {
            try {
                setIsLoadingUnits(true);
                const response = await apiService.getUnits();
                const processedUnits = response.data || response || [];
                setUnits(processedUnits);
            } catch (error) {
                console.error("Error fetching units in Business Settings:", error);
            } finally {
                setIsLoadingUnits(false);
            }
        };

        // Only fetch when the product tab is clicked
        if (activeTab === 'product' && units.length === 0) {
            fetchUnits();
        }
    }, [activeTab, units.length]);

    // Fetch tax rates for the sale tab
    useEffect(() => {
        const fetchTaxRates = async () => {
            try {
                setIsLoadingTaxRates(true);
                const response = await apiService.getTaxRates();
                const processedTaxes = response.data || response || [];
                setTaxRates(processedTaxes);
            } catch (error) {
                console.error("Error fetching tax rates in Business Settings:", error);
            } finally {
                setIsLoadingTaxRates(false);
            }
        };

        if (activeTab === 'sale' && taxRates.length === 0) {
            fetchTaxRates();
        }
    }, [activeTab, taxRates.length]);

    // Fetch QB connection status when quickbooks tab is activated
    useEffect(() => {
        if (activeTab === 'quickbooks') {
            fetchQbConnection().then(() => {
                fetchQbAccounts();
            });
        }
    }, [activeTab]);

    // Form Content Renderer
    const renderTabContent = () => {
        switch (activeTab) {
            case 'business':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        <div className="settings-grid-3">
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Business Name:*</label>
                                <input type="text" defaultValue="Lefty\'S Liquor" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Start Date:</label>
                                <input type="date" defaultValue="2024-08-26" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Default profit percent:* ⓘ</label>
                                <input type="number" defaultValue="30.00" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Currency:</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>United States of America - Dol...</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Currency Symbol:*</label>
                                <input type="text" defaultValue="$" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Currency Symbol Placement:</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>Before amount</option>
                                    <option>After amount</option>
                                </select>
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Time zone:</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>America/Chicago</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Upload Logo:</label>
                                <div style={{ display: 'flex' }}>
                                    <input type="file" id="logo-upload" style={{ display: 'none' }} />
                                    <input type="text" readOnly style={{ flex: 1, padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px 0 0 4px', borderRight: 'none', background: '#f8fafc' }} />
                                    <label htmlFor="logo-upload" style={{ padding: '0.65rem 1rem', background: '#2563eb', color: 'white', borderRadius: '0 4px 4px 0', cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        Browse..
                                    </label>
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem', fontStyle: 'italic' }}>Previous logo (if exists) will be replaced</div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Financial year start month: ⓘ</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>January</option>
                                    <option>February</option>
                                    <option>March</option>
                                    <option>April</option>
                                    <option>May</option>
                                    <option>June</option>
                                    <option>July</option>
                                    <option>August</option>
                                    <option>September</option>
                                    <option>October</option>
                                    <option>November</option>
                                    <option>December</option>
                                </select>
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Stock Accounting Method:* ⓘ</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>FIFO (First In First Out)</option>
                                    <option>LIFO (Last In First Out)</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Transaction Edit Days:* ⓘ</label>
                                <div style={{ display: 'flex' }}>
                                    <span style={{ padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRight: 'none', background: '#f8fafc', borderRadius: '4px 0 0 4px', color: '#64748b' }}>📝</span>
                                    <input type="number" defaultValue="30" style={{ flex: 1, padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '0 4px 4px 0', outline: 'none' }} />
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Date Format:*</label>
                                <div style={{ display: 'flex' }}>
                                    <span style={{ padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRight: 'none', background: '#f8fafc', borderRadius: '4px 0 0 4px', color: '#64748b' }}>📅</span>
                                    <select style={{ flex: 1, padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '0 4px 4px 0', outline: 'none' }}>
                                        <option>mm/dd/yyyy</option>
                                        <option>dd/mm/yyyy</option>
                                        <option>dd-mm-yyyy</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Time Format:*</label>
                                <div style={{ display: 'flex' }}>
                                    <span style={{ padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRight: 'none', background: '#f8fafc', borderRadius: '4px 0 0 4px', color: '#64748b' }}>🕒</span>
                                    <select style={{ flex: 1, padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '0 4px 4px 0', outline: 'none' }}>
                                        <option>12 Hour</option>
                                        <option>24 Hour</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Currency precision:* ⓘ</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} defaultValue="2">
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Quantity precision:* ⓘ</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} defaultValue="2">
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                </select>
                            </div>
                        </div>
                    </div>
                );
            case 'tax':
                return (
                    <div className="settings-grid-3">
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Tax 1 Name:</label><input type="text" placeholder="GST / VAT / Other" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Tax 1 No.:</label><input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div />

                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Tax 2 Name:</label><input type="text" placeholder="GST / VAT / Other" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Tax 2 No.:</label><input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div />

                        <div style={{ gridColumn: 'span 3', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                            <input type="checkbox" style={{ width: '18px', height: '18px' }} />
                            <label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Enable inline tax in purchase and sell</label>
                        </div>
                    </div>
                );
            case 'product':
                return (
                    <div className="settings-grid-3">
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>SKU prefix:</label><input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>

                        <div className="settings-span-2 flex-col-05">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" style={{ width: '16px', height: '16px' }} /><label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Enable Product Expiry</label></div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" style={{ width: '16px', height: '16px' }} /><label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Add item expiry</label></div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" defaultChecked style={{ width: '16px', height: '16px' }} /><label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Enable Brands</label></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" defaultChecked style={{ width: '16px', height: '16px' }} /><label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Enable Categories</label></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" defaultChecked style={{ width: '16px', height: '16px' }} /><label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Enable Sub-Categories</label></div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', gridColumn: 'span 3' }}><input type="checkbox" defaultChecked style={{ width: '16px', height: '16px' }} /><label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Enable Price & Tax info</label></div>

                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Default Unit:</label>
                            <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} disabled={isLoadingUnits}>
                                <option value="">Please Select</option>
                                {units.map(unit => (
                                    <option key={unit.id} value={unit.id}>{unit.actual_name || unit.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="settings-span-2"></div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" style={{ width: '16px', height: '16px' }} /><label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Enable Sub Units</label></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" style={{ width: '16px', height: '16px' }} /><label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Enable Racks</label></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" style={{ width: '16px', height: '16px' }} /><label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Enable Row</label></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" style={{ width: '16px', height: '16px' }} /><label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Enable Position</label></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" style={{ width: '16px', height: '16px' }} /><label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Enable Warranty</label></div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', gridColumn: 'span 3', marginTop: '1rem' }}><input type="checkbox" style={{ width: '16px', height: '16px' }} /><label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Is product image required?</label></div>
                    </div>
                );
            case 'contact':
                return (
                    <div className="settings-grid-3">
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Default credit limit:</label>
                            <input type="number" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                        </div>
                    </div>
                );
            case 'sale':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

                        {/* Top General Section */}
                        <div className="settings-grid-3">
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Default Sale Discount:*</label>
                                <input type="number" defaultValue="0.00" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Default Sale Tax:</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} disabled={isLoadingTaxRates}>
                                    <option value="">None</option>
                                    {taxRates.map(tax => (
                                        <option key={tax.id} value={tax.id}>{tax.name || tax.description}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Sales Item Addition Method:</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>Please Select</option>
                                    <option>Add item in new row</option>
                                    <option>Increase item quantity if it already exists</option>
                                </select>
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Amount rounding method:</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>Please Select</option>
                                    <option>Round to nearest whole number</option>
                                </select>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
                                <input type="checkbox" style={{ width: '16px', height: '16px' }} />
                                <label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Sales price is minimum selling price</label>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
                                <input type="checkbox" style={{ width: '16px', height: '16px' }} />
                                <label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Allow Overselling</label>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input type="checkbox" style={{ width: '16px', height: '16px' }} />
                                <label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Enable Sales Order</label>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input type="checkbox" style={{ width: '16px', height: '16px' }} />
                                <label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Is pay term required?</label>
                            </div>
                            <div />

                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Commission Agent:</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>Please Select</option>
                                    <option>Disable</option>
                                    <option>Logged in User</option>
                                    <option>Select from users</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Sales Commission Agent:</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>Please Select</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Commission Calculation Type:</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>Please Select</option>
                                    <option>Percentage</option>
                                    <option>Fixed</option>
                                </select>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', gridColumn: 'span 3' }}>
                                <input type="checkbox" style={{ width: '16px', height: '16px' }} />
                                <label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>Is commission agent required?</label>
                            </div>
                        </div>

                        <hr style={{ border: 0, borderTop: '1px solid var(--etpos-border)' }} />

                        {/* Payment Links Section */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <h4 style={{ margin: 0, color: 'var(--etpos-text)', fontSize: '1.1rem' }}>Payment Link</h4>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input type="checkbox" style={{ width: '16px', height: '16px' }} />
                                <label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)', fontWeight: 'bold' }}>Enable payment link</label>
                            </div>

                            <div style={{ padding: '1.5rem', background: 'var(--etpos-bg)', border: '1px solid var(--etpos-border)', borderRadius: '6px', marginTop: '0.5rem' }}>
                                <h5 style={{ margin: '0 0 1rem 0', color: 'var(--etpos-text)' }}>Razorpay: (For INR India)</h5>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Key ID:</label>
                                        <input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Key Secret:</label>
                                        <input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                                    </div>
                                </div>
                            </div>

                            <div style={{ padding: '1.5rem', background: 'var(--etpos-bg)', border: '1px solid var(--etpos-border)', borderRadius: '6px', marginTop: '0.5rem' }}>
                                <h5 style={{ margin: '0 0 1rem 0', color: 'var(--etpos-text)' }}>Stripe:</h5>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Stripe public key:</label>
                                        <input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Stripe secret key:</label>
                                        <input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'pos':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

                        {/* Keyboard Shortcuts Section */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                                <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--etpos-text)', fontSize: '1.1rem' }}>Add keyboard shortcuts:</h4>
                                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--etpos-text-muted)' }}>
                                    Shortcut should be the names of the keys separated by '+'; Example: <code style={{ background: 'var(--etpos-bg)', padding: '0.1rem 0.3rem', borderRadius: '3px' }}>ctrl+shift+b</code>, <code style={{ background: 'var(--etpos-bg)', padding: '0.1rem 0.3rem', borderRadius: '3px' }}>ctrl+h</code><br />
                                    Available key names are: shift, ctrl, alt, backspace, tab, enter, return, capslock, esc, escape, space, pageup, pagedown, end, home, left, up, right, down, ins, del, and plus
                                </p>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                                {/* Table 1 */}
                                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid var(--etpos-border)' }}>
                                    <thead style={{ background: 'var(--etpos-bg)' }}>
                                        <tr>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--etpos-border)', fontSize: '0.9rem', color: 'var(--etpos-text-muted)', fontWeight: '600' }}>Operations</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--etpos-border)', fontSize: '0.9rem', color: 'var(--etpos-text-muted)', fontWeight: '600' }}>Keyboard Shortcut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { label: 'Express Checkout:', default: 'shift+e' },
                                            { label: 'Pay & Checkout:', default: 'shift+p' },
                                            { label: 'Draft:', default: 'shift+d' },
                                            { label: 'Cancel:', default: '' },
                                            { label: 'Go to product quantity:', default: 'f2' },
                                            { label: 'Weighing Scale:', default: '' }
                                        ].map((row, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                                                <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: 'var(--etpos-text)', fontWeight: '500' }}>{row.label}</td>
                                                <td style={{ padding: '0.75rem' }}><input type="text" defaultValue={row.default} style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* Table 2 */}
                                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid var(--etpos-border)' }}>
                                    <thead style={{ background: 'var(--etpos-bg)' }}>
                                        <tr>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--etpos-border)', fontSize: '0.9rem', color: 'var(--etpos-text-muted)', fontWeight: '600' }}>Operations</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--etpos-border)', fontSize: '0.9rem', color: 'var(--etpos-text-muted)', fontWeight: '600' }}>Keyboard Shortcut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { label: 'Edit Discount:', default: 'shift+i' },
                                            { label: 'Edit Order Tax:', default: 'shift+t' },
                                            { label: 'Add Payment Row:', default: 'shift+r' },
                                            { label: 'Finalize Payment:', default: 'shift+f' },
                                            { label: 'Add new product:', default: 'f4' }
                                        ].map((row, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                                                <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: 'var(--etpos-text)', fontWeight: '500' }}>{row.label}</td>
                                                <td style={{ padding: '0.75rem' }}><input type="text" defaultValue={row.default} style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <hr style={{ border: 0, borderTop: '1px solid var(--etpos-border)' }} />

                        {/* POS Settings Section */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <h4 style={{ margin: 0, color: 'var(--etpos-text)', fontSize: '1.1rem' }}>POS settings:</h4>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                                {[
                                    'Disable Multiple Pay', 'Disable Draft', 'Disable Express Checkout',
                                    "Don't show product suggestion", "Don't show recent transactions",
                                    'Disable Discount', 'Disable order tax', 'Subtotal Editable',
                                    'Disable Suspend Sale', 'Enable transaction date on POS screen',
                                    'Enable service staff in product line', 'Is service staff required',
                                    'Disable credit sale button', 'Enable Weighing Scale',
                                    'Show invoice scheme', 'Show invoice layout dropdown',
                                    'Print invoice on suspend', 'Show pricing on product suggestion tooltip'
                                ].map((setting, idx) => (
                                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input type="checkbox" style={{ width: '16px', height: '16px' }} />
                                        <label style={{ fontSize: '0.9rem', color: 'var(--etpos-text)' }}>{setting}</label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <hr style={{ border: 0, borderTop: '1px solid var(--etpos-border)' }} />

                        {/* Weighing Scale Section */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                                <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--etpos-text)', fontSize: '1.1rem' }}>Weighing Scale barcode Setting:</h4>
                                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--etpos-text-muted)' }}>Configure barcode as per your weighing scale.</p>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1.5rem' }}>
                                <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Prefix:</label><input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                                <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Product sku length:</label><input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                                <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Quantity integer part length:</label><input type="number" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                                <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--etpos-text-muted)' }}>Quantity fractional part length:</label><input type="number" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                            </div>
                        </div>

                    </div>
                );
            case 'purchases':
                return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input type="checkbox" defaultChecked style={{ width: '18px', height: '18px' }} />
                            <label style={{ fontSize: '0.95rem', color: 'var(--etpos-text)' }}>Enable editing product price from purchase screen</label>
                            <span style={{ color: '#0ea5e9', cursor: 'pointer', fontSize: '1rem', marginLeft: '0.2rem' }}>ⓘ</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input type="checkbox" defaultChecked style={{ width: '18px', height: '18px' }} />
                            <label style={{ fontSize: '0.95rem', color: 'var(--etpos-text)' }}>Enable Purchase Status</label>
                            <span style={{ color: '#0ea5e9', cursor: 'pointer', fontSize: '1rem', marginLeft: '0.2rem' }}>ⓘ</span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input type="checkbox" style={{ width: '18px', height: '18px' }} />
                            <label style={{ fontSize: '0.95rem', color: 'var(--etpos-text)' }}>Enable Lot number</label>
                            <span style={{ color: '#0ea5e9', cursor: 'pointer', fontSize: '1rem', marginLeft: '0.2rem' }}>ⓘ</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input type="checkbox" defaultChecked style={{ width: '18px', height: '18px' }} />
                            <label style={{ fontSize: '0.95rem', color: 'var(--etpos-text)' }}>Enable purchase order</label>
                            <span style={{ color: '#0ea5e9', cursor: 'pointer', fontSize: '1rem', marginLeft: '0.2rem' }}>ⓘ</span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input type="checkbox" defaultChecked style={{ width: '18px', height: '18px' }} />
                            <label style={{ fontSize: '0.95rem', color: 'var(--etpos-text)' }}>Enable Purchase Requisition</label>
                            <span style={{ color: '#0ea5e9', cursor: 'pointer', fontSize: '1rem', marginLeft: '0.2rem' }}>ⓘ</span>
                        </div>
                    </div>
                );
            case 'payment':
                return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2rem' }}>

                        {/* Column 1 */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Cash Denominations:</label>
                                <input type="text" defaultValue=".01, .05, .10, .25, 1, 5, 10, 20, 50, 100" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                                <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.8rem', color: 'var(--etpos-text-muted)' }}>Comma separated values Example:<br />100,200,500,2000</p>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Buisness Check Return Charges</label>
                                <input type="number" defaultValue="35" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>
                        </div>

                        {/* Column 2 */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Enable cash denomination on:</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>POS screen</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Bank Check Return Charges</label>
                                <input type="number" defaultValue="22" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>
                        </div>

                        {/* Column 3 */}
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Enable cash denomination for payment methods:</label>

                            <div style={{ position: 'relative' }}>
                                <div
                                    style={{ border: '1px solid #3b82f6', borderRadius: '4px', display: 'flex', gap: '0.4rem', padding: '0.5rem', background: 'var(--etpos-card)', flexWrap: 'wrap', cursor: 'pointer', minHeight: '42px' }}
                                    onClick={() => setIsPaymentMethodDropdownOpen(!isPaymentMethodDropdownOpen)}
                                >
                                    {selectedPaymentMethods.length === 0 ? (
                                        <span style={{ color: 'var(--etpos-text-muted)', fontSize: '0.9rem' }}>Select methods...</span>
                                    ) : (
                                        selectedPaymentMethods.map(method => (
                                            <span
                                                key={method}
                                                style={{ background: '#3b82f6', color: 'white', padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedPaymentMethods(prev => prev.filter(m => m !== method));
                                                }}
                                            >
                                                <span style={{ fontSize: '1rem', lineHeight: 1 }}>×</span> {method}
                                            </span>
                                        ))
                                    )}
                                </div>

                                {/* Dropdown List Area */}
                                {isPaymentMethodDropdownOpen && (
                                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, border: '1px solid var(--etpos-border)', borderTop: 'none', borderRadius: '0 0 4px 4px', background: 'var(--etpos-card)', maxHeight: '150px', overflowY: 'auto', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                                        {['Cash', 'Card', 'Cheque', 'Bank Transfer', 'Other', 'Custom Payment 1'].map(method => (
                                            <div
                                                key={method}
                                                style={{
                                                    padding: '0.5rem 0.75rem',
                                                    fontSize: '0.9rem',
                                                    cursor: 'pointer',
                                                    borderBottom: '1px solid var(--etpos-bg)',
                                                    color: selectedPaymentMethods.includes(method) ? 'white' : 'var(--etpos-text)',
                                                    background: selectedPaymentMethods.includes(method) ? '#3b82f6' : 'transparent'
                                                }}
                                                onClick={() => {
                                                    if (selectedPaymentMethods.includes(method)) {
                                                        setSelectedPaymentMethods(prev => prev.filter(m => m !== method));
                                                    } else {
                                                        setSelectedPaymentMethods(prev => [...prev, method]);
                                                    }
                                                    setIsPaymentMethodDropdownOpen(false);
                                                }}
                                            >
                                                {method}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                );
            case 'dashboard':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>View Stock Expiry Alert For:*</label>
                            <div style={{ display: 'flex', width: '300px' }}>
                                <div style={{ padding: '0.65rem', background: 'var(--etpos-bg)', border: '1px solid var(--etpos-border)', borderRight: 'none', borderRadius: '4px 0 0 4px', color: 'var(--etpos-text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    📅 {/* Fallback calendar icon */}
                                </div>
                                <input type="number" defaultValue="30" style={{ flex: 1, padding: '0.65rem', border: '1px solid var(--etpos-border)', borderLeft: 'none', borderRight: 'none', outline: 'none' }} />
                                <div style={{ padding: '0.65rem', background: 'var(--etpos-bg)', border: '1px solid var(--etpos-border)', borderRadius: '0 4px 4px 0', color: 'var(--etpos-text-muted)', fontSize: '0.9rem' }}>
                                    Days
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'system':
                return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2rem' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Theme Color</label>
                            <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                <option>Green</option>
                                <option>Blue</option>
                                <option>Red</option>
                            </select>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Default datatable page entries</label>
                            <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                <option>25</option>
                                <option>50</option>
                                <option>100</option>
                            </select>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.25rem' }}>
                            <input type="checkbox" defaultChecked style={{ width: '18px', height: '18px' }} />
                            <label style={{ fontSize: '0.95rem', color: 'var(--etpos-text)' }}>Show help text</label>
                        </div>
                    </div>
                );
            case 'prefixes':
                return (
                    <div className="settings-grid-3">
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Purchase:</label><input type="text" defaultValue="PO" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Purchase Return:</label><input type="text" defaultValue="PR" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Purchase Requisition:</label><input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>

                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Purchase Order:</label><input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Stock Transfer:</label><input type="text" defaultValue="ST" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Stock Adjustment:</label><input type="text" defaultValue="SA" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>

                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Sell Return:</label><input type="text" defaultValue="CN" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Expenses:</label><input type="text" defaultValue="EP" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Contacts:</label><input type="text" defaultValue="CO" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>

                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Purchase Payment:</label><input type="text" defaultValue="PP" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Sell Payment:</label><input type="text" defaultValue="SP" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Expense Payment:</label><input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>

                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Business Location:</label><input type="text" defaultValue="BL" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Username:</label><input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Subscription No.:</label><input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>

                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Draft:</label><input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                        <div><label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.2rem', color: 'var(--etpos-text)' }}>Sales Order:</label><input type="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} /></div>
                    </div>
                );
            case 'sms-settings':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        <div className="settings-grid-3">
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>SMS Service:</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>Other</option>
                                    <option>Twilio</option>
                                    <option>Nexmo</option>
                                </select>
                            </div>
                            <div className="settings-span-2">
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>URL:</label>
                                <input type="text" placeholder="URL" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Send to parameter name:</label>
                                <input type="text" defaultValue="to" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Message parameter name:</label>
                                <input type="text" defaultValue="text" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Request Method:</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>POST</option>
                                    <option>GET</option>
                                </select>
                            </div>
                        </div>

                        <hr style={{ border: 0, borderTop: '1px solid var(--etpos-border)' }} />

                        {/* Headers Section */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h4 style={{ margin: 0, color: 'var(--etpos-text)', fontSize: '1rem' }}>Headers:</h4>
                                <button
                                    onClick={addSmsHeader}
                                    style={{ padding: '0.4rem 0.8rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer' }}
                                >
                                    + Add Header
                                </button>
                            </div>

                            {smsHeaders.map((header) => (
                                <div key={header.id} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.3rem', color: 'var(--etpos-text-muted)' }}>Key:</label>
                                        <input type="text" placeholder="e.g. Authorization" style={{ width: '100%', padding: '0.6rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.3rem', color: 'var(--etpos-text-muted)' }}>Value:</label>
                                        <input type="text" placeholder="Value" style={{ width: '100%', padding: '0.6rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                                    </div>
                                    <button
                                        onClick={() => removeSmsHeader(header.id)}
                                        style={{ padding: '0.6rem', background: '#fee2e2', color: '#ef4444', border: '1px solid var(--etpos-border)', borderRadius: '4px', cursor: 'pointer' }}
                                        title="Remove"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>

                        <hr style={{ border: 0, borderTop: '1px solid var(--etpos-border)' }} />

                        {/* Parameters Section */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h4 style={{ margin: 0, color: 'var(--etpos-text)', fontSize: '1rem' }}>Parameters:</h4>
                                <button
                                    onClick={addSmsParam}
                                    style={{ padding: '0.4rem 0.8rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer' }}
                                >
                                    + Add Parameter
                                </button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {smsParams.map((param) => (
                                    <div key={param.id} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.3rem', color: 'var(--etpos-text-muted)' }}>Key:</label>
                                            <input type="text" placeholder="Key" style={{ width: '100%', padding: '0.6rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.3rem', color: 'var(--etpos-text-muted)' }}>Value:</label>
                                            <input type="text" placeholder="Value" style={{ width: '100%', padding: '0.6rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                                        </div>
                                        <button
                                            onClick={() => removeSmsParam(param.id)}
                                            style={{ padding: '0.6rem', background: '#fee2e2', color: '#ef4444', border: '1px solid var(--etpos-border)', borderRadius: '4px', cursor: 'pointer' }}
                                            title="Remove"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: '1rem' }}>
                            <div style={{ width: '300px' }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Test Number:</label>
                                <input type="text" placeholder="Enter test number" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>
                            <button
                                style={{
                                    background: '#10b981',
                                    color: 'white',
                                    padding: '0.75rem 2rem',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    cursor: 'pointer',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                                onClick={() => alert('Sending test SMS...')}
                            >
                                Send test SMS
                            </button>
                        </div>
                    </div>
                );
            case 'email-settings':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        <div className="settings-grid-3">
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Mail Driver:</label>
                                <select style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }}>
                                    <option>SMTP</option>
                                    <option>Sndgrid</option>
                                    <option>Mailgun</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Host:</label>
                                <input type="text" placeholder="smtp.hostinger.com" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Port:</label>
                                <input type="text" placeholder="587" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Username:</label>
                                <input type="text" placeholder="info@balajienterprise2022llc.com" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Password:</label>
                                <input type="password" placeholder="••••••••••••••••" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>Encryption:</label>
                                <input type="text" placeholder="TLS" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>From Address:</label>
                                <input type="text" placeholder="info@balajienterprise2022llc.com" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>From Name:</label>
                                <input type="text" placeholder="Balaji Enterprises 2022" style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px' }} />
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                            <button
                                style={{
                                    background: '#10b981',
                                    color: 'white',
                                    padding: '0.75rem 2rem',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    cursor: 'pointer',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                                onClick={() => alert('Sending test email...')}
                            >
                                Send test email
                            </button>
                        </div>
                    </div>
                );
            case 'reward-point-settings':
                const InfoIcon = () => (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        background: '#38bdf8',
                        color: 'white',
                        fontSize: '10px',
                        marginLeft: '4px',
                        cursor: 'help'
                    }}>i</span>
                );

                const RewardFieldLabel = ({ title, showInfo = true }) => (
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>
                        {title} {showInfo && <InfoIcon />}
                    </label>
                );

                const rewardInputStyle = { width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '4px', background: 'var(--etpos-card)' };

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        {/* Section 1: Enable & Display Name */}
                        <div style={{ padding: '1.5rem', background: 'var(--etpos-bg)', border: '1px solid var(--etpos-border)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '4rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--etpos-text)', cursor: 'pointer' }}>
                                <input type="checkbox" defaultChecked style={{ width: '18px', height: '18px' }} />
                                Enable Reward Point
                            </label>
                            <div style={{ flex: 1, maxWidth: '400px' }}>
                                <RewardFieldLabel title="Reward Point Display Name:" showInfo={false} />
                                <input type="text" defaultValue="Lefty's Rewards" style={rewardInputStyle} />
                            </div>
                        </div>

                        {/* Section 2: Earning Points Settings */}
                        <div style={{ padding: '1.5rem', background: 'var(--etpos-bg)', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
                            <h4 style={{ margin: '0 0 1.5rem 0', color: '#3b82f6', fontSize: '1.1rem', fontWeight: '500' }}>Earning Points Settings:</h4>
                            <div className="settings-grid-3">
                                <div>
                                    <RewardFieldLabel title="Amount spend for unit point:" />
                                    <input type="text" defaultValue="1.00" style={rewardInputStyle} />
                                </div>
                                <div>
                                    <RewardFieldLabel title="Minimum order total to earn reward:" />
                                    <input type="text" defaultValue="1.00" style={rewardInputStyle} />
                                </div>
                                <div>
                                    <RewardFieldLabel title="Maximum points per order:" />
                                    <input type="text" placeholder="Maximum points per order" style={rewardInputStyle} />
                                </div>
                            </div>
                        </div>

                        {/* Section 3: Redeem Points Settings */}
                        <div style={{ padding: '1.5rem', background: 'var(--etpos-bg)', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
                            <h4 style={{ margin: '0 0 1.5rem 0', color: '#3b82f6', fontSize: '1.1rem', fontWeight: '500' }}>Redeem Points Settings:</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                <div className="settings-grid-3">
                                    <div>
                                        <RewardFieldLabel title="Redeem amount per unit point:" />
                                        <input type="text" defaultValue="1.00" style={rewardInputStyle} />
                                    </div>
                                    <div>
                                        <RewardFieldLabel title="Minimum order total to redeem points:" />
                                        <input type="text" defaultValue="1.00" style={rewardInputStyle} />
                                    </div>
                                    <div>
                                        <RewardFieldLabel title="Minimum redeem point:" />
                                        <input type="text" placeholder="Minimum redeem point" style={rewardInputStyle} />
                                    </div>
                                </div>
                                <div className="settings-grid-3">
                                    <div>
                                        <RewardFieldLabel title="Maximum redeem point per order:" />
                                        <input type="text" placeholder="Maximum redeem point per order" style={rewardInputStyle} />
                                    </div>
                                    <div style={{ gridColumn: 'span 1' }}>
                                        <RewardFieldLabel title="Reward Point expiry period:" />
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <input type="text" placeholder="Reward Point expiry period" style={{ ...rewardInputStyle, flex: 2 }} />
                                            <select style={{ ...rewardInputStyle, flex: 1 }}>
                                                <option>Year</option>
                                                <option>Month</option>
                                                <option>Day</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'modules':
                const ModuleInfoIcon = () => (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        background: '#38bdf8',
                        color: 'white',
                        fontSize: '10px',
                        marginLeft: '6px',
                        cursor: 'help'
                    }}>i</span>
                );

                const moduleItems = [
                    { label: 'Purchases', checked: true },
                    { label: 'Add Sale', checked: true },
                    { label: 'POS', checked: true },
                    { label: 'Stock Transfers', checked: true },
                    { label: 'Stock Adjustment', checked: true },
                    { label: 'Expenses', checked: true },
                    { label: 'Account', checked: true },
                    { label: 'Tables', checked: false, info: true },
                    { label: 'Modifiers', checked: false, info: true },
                    { label: 'Service staff', checked: true, info: true },
                    { label: 'Enable Bookings', checked: false },
                    { label: 'Kitchen (For restaurants)', checked: false },
                    { label: 'Enable Subscription', checked: false },
                    { label: 'Types of service', checked: false, info: true }
                ];

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        <h3 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--etpos-text)', fontWeight: '500' }}>Enable/Disable Modules</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', rowGap: '2rem', columnGap: '1.5rem' }}>
                            {moduleItems.map((m, idx) => (
                                <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.9rem', color: 'var(--etpos-text)', cursor: 'pointer', fontWeight: '400' }}>
                                    <input
                                        type="checkbox"
                                        defaultChecked={m.checked}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                        {m.label}
                                        {m.info && <ModuleInfoIcon />}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                );
            case 'custom-labels':
                const CollapsibleSection = ({ sectionKey, title, fields, setter, renderRow }) => {
                    const isOpen = expandedSections[sectionKey];
                    return (
                        <div style={{
                            marginBottom: '1rem',
                            border: '1px solid var(--etpos-border)',
                            borderRadius: '8px',
                            background: 'var(--etpos-card)',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                            overflow: 'hidden'
                        }}>
                            <div
                                onClick={() => toggleSection(sectionKey)}
                                style={{
                                    padding: '1rem 1.5rem',
                                    background: isOpen ? 'var(--etpos-bg)' : 'var(--etpos-card)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    borderBottom: isOpen ? '1px solid var(--etpos-border)' : 'none',
                                    transition: 'background 0.2s'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <span style={{
                                        color: isOpen ? '#3b82f6' : 'var(--etpos-text-muted)',
                                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s',
                                        fontSize: '0.6rem'
                                    }}>▶</span>
                                    <h4 style={{ margin: 0, color: 'var(--etpos-text)', fontSize: '0.95rem', fontWeight: '600' }}>
                                        {title}
                                        <span style={{ marginLeft: '0.75rem', fontSize: '0.75rem', color: 'var(--etpos-text-muted)', fontWeight: '400' }}>
                                            ({fields.length} Fields)
                                        </span>
                                    </h4>
                                </div>
                                <button
                                    onClick={(e) => { e.stopPropagation(); addField(setter); if (!isOpen) toggleSection(sectionKey); }}
                                    style={{
                                        padding: '0.3rem 0.6rem',
                                        background: '#3b82f6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '0.7rem',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem'
                                    }}
                                >
                                    <span>+</span> Add Row
                                </button>
                            </div>

                            {isOpen && (
                                <div style={{ padding: '1.5rem', display: 'grid', gap: '1rem', background: 'var(--etpos-card)' }}>
                                    {fields.map((f, i) => renderRow(f, i, setter))}
                                </div>
                            )}
                        </div>
                    );
                };

                const customInputStyle = { width: '100%', padding: '0.6rem', border: '1px solid var(--etpos-border)', borderRadius: '4px', fontSize: '0.875rem' };

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <CollapsibleSection
                            sectionKey="pay"
                            title="Labels for custom payments"
                            fields={payLabels}
                            setter={setPayLabels}
                            renderRow={(f, i, setter) => (
                                <div key={f.id} style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                    <input type="text" placeholder={`Custom Payment ${i + 1}`} style={customInputStyle} />
                                    <button onClick={() => removeField(setter, f.id)} style={{ padding: '0.5rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                                </div>
                            )}
                        />

                        <CollapsibleSection
                            sectionKey="contact"
                            title="Labels for contact custom fields"
                            fields={contactFields}
                            setter={setContactFields}
                            renderRow={(f, i, setter) => (
                                <div key={f.id} style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                    <input type="text" placeholder={`Custom Field ${i + 1}`} style={customInputStyle} />
                                    <button onClick={() => removeField(setter, f.id)} style={{ padding: '0.5rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                                </div>
                            )}
                        />

                        <CollapsibleSection
                            sectionKey="product"
                            title="Labels for product custom fields"
                            fields={productFields}
                            setter={setProductFields}
                            renderRow={(f, i, setter) => (
                                <div key={f.id} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                                    <div style={{ flex: 2 }}>
                                        <label style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted)', marginBottom: '0.25rem', display: 'block' }}>Custom Field {i + 1}:</label>
                                        <input type="text" style={customInputStyle} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted)', marginBottom: '0.25rem', display: 'block' }}>Field Type:</label>
                                        <select style={customInputStyle}>
                                            <option>Text</option><option>Date</option><option>Number</option><option>Dropdown</option>
                                        </select>
                                    </div>
                                    <button onClick={() => removeField(setter, f.id)} style={{ padding: '0.6rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                                </div>
                            )}
                        />

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <CollapsibleSection
                                sectionKey="loc" title="Location Fields" fields={locationFields} setter={setLocationFields}
                                renderRow={(f, i, setter) => (
                                    <div key={f.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                        <input type="text" placeholder={`Field ${i + 1}`} style={customInputStyle} />
                                        <button onClick={() => removeField(setter, f.id)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                                    </div>
                                )}
                            />
                            <CollapsibleSection
                                sectionKey="user" title="User Fields" fields={userFields} setter={setUserFields}
                                renderRow={(f, i, setter) => (
                                    <div key={f.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                        <input type="text" placeholder={`Field ${i + 1}`} style={customInputStyle} />
                                        <button onClick={() => removeField(setter, f.id)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                                    </div>
                                )}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <CollapsibleSection
                                sectionKey="pur" title="Purchase Fields" fields={purchaseFields} setter={setPurchaseFields}
                                renderRow={(f, i, setter) => (
                                    <div key={f.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <input type="text" placeholder={`Field ${i + 1}`} style={customInputStyle} />
                                        <label style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap' }}>
                                            <input type="checkbox" /> Required
                                        </label>
                                        <button onClick={() => removeField(setter, f.id)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                                    </div>
                                )}
                            />
                            <CollapsibleSection
                                sectionKey="purShip" title="Purchase Ship Fields" fields={purchaseShipFields} setter={setPurchaseShipFields}
                                renderRow={(f, i, setter) => (
                                    <div key={f.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <input type="text" placeholder={`Field ${i + 1}`} style={customInputStyle} />
                                        <label style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap' }}>
                                            <input type="checkbox" /> Required
                                        </label>
                                        <button onClick={() => removeField(setter, f.id)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                                    </div>
                                )}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <CollapsibleSection
                                sectionKey="sell" title="Sell Fields" fields={sellFields} setter={setSellFields}
                                renderRow={(f, i, setter) => (
                                    <div key={f.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <input type="text" placeholder={`Field ${i + 1}`} style={customInputStyle} />
                                        <label style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap' }}>
                                            <input type="checkbox" /> Required
                                        </label>
                                        <button onClick={() => removeField(setter, f.id)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                                    </div>
                                )}
                            />
                            <CollapsibleSection
                                sectionKey="sellShip" title="Sale Ship Fields" fields={sellShipFields} setter={setSellShipFields}
                                renderRow={(f, i, setter) => (
                                    <div key={f.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <input type="text" placeholder={`Field ${i + 1}`} style={customInputStyle} />
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <label style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}><input type="checkbox" /> Required</label>
                                            <label style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}><input type="checkbox" /> Default</label>
                                        </div>
                                        <button onClick={() => removeField(setter, f.id)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                                    </div>
                                )}
                            />
                        </div>

                        <CollapsibleSection
                            sectionKey="svc" title="Types of Service" fields={serviceFields} setter={setServiceFields}
                            renderRow={(f, i, setter) => (
                                <div key={f.id} style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                    <input type="text" placeholder={`Field ${i + 1}`} style={customInputStyle} />
                                    <button onClick={() => removeField(setter, f.id)} style={{ padding: '0.5rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                                </div>
                            )}
                        />
                    </div>
                );
            case 'quickbooks':
                const qbBtnStyle = (bg) => ({
                                    background: bg, color: 'white', padding: '0.75rem 1rem', border: 'none',
                                    borderRadius: '6px', fontWeight: '600', fontSize: '0.9rem', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                                    cursor: qbLoading ? 'not-allowed' : 'pointer', opacity: qbLoading ? 0.7 : 1,
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'opacity 0.2s'
                                });
                                const qbSelectStyle = {
                                    width: '100%', padding: '0.65rem',
                                    border: '1px solid var(--etpos-border)', borderRadius: '4px',
                                    background: 'var(--etpos-card)', color: 'var(--etpos-text)'
                                };
                                const qbAccountOptions = qbAccounts.map(acc => (
                                    <option key={acc.Id} value={acc.Id}>
                                        {acc.Name} ({acc.AccountType})
                                    </option>
                                ));
                                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        <div>
                            <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.5rem', color: 'var(--etpos-text)', fontWeight: '500' }}>
                                QuickBooks {qbConnectionData?.environment === 'production' ? 'Production' : 'Sandbox'} Integration
                            </h3>

                            {/* Loading state */}
                            {qbLoading && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#3b82f6', marginBottom: '1rem', fontSize: '0.9rem' }}>
                                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Connecting to QuickBooks...
                                </div>
                            )}

                            {/* Connection Status Box */}
                            {isConnected ? (
                                <div style={{ background: '#10b981', color: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', marginBottom: '1.5rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: '600', fontSize: '1.1rem' }}>
                                        <CheckCircle size={20} />
                                        <span>Active Connection Found</span>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', fontSize: '0.9rem' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                            <div><span style={{ opacity: 0.85, fontWeight: '500' }}>Company Name:</span> <span style={{ fontWeight: '600' }}>{qbConnectionData?.company_name || 'N/A'}</span></div>
                                            <div><span style={{ opacity: 0.85, fontWeight: '500' }}>Realm ID:</span> <span style={{ fontWeight: '600' }}>{qbConnectionData?.realm_id || qbConnectionData?.provider_id || '—'}</span></div>
                                            <div><span style={{ opacity: 0.85, fontWeight: '500' }}>Environment:</span> <span style={{ fontWeight: '600' }}>{qbConnectionData?.environment || 'sandbox'}</span></div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', textAlign: 'right' }}>
                                            <div><span style={{ opacity: 0.85, fontWeight: '500' }}>Token Expires:</span> <span style={{ fontWeight: '600' }}>{qbConnectionData?.token_expires_at ? new Date(qbConnectionData.token_expires_at).toLocaleString() : '—'}</span></div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ background: 'var(--etpos-bg)', border: '1px dashed var(--etpos-border)', color: 'var(--etpos-text-muted)', padding: '2rem', borderRadius: '8px', textAlign: 'center', marginBottom: '1.5rem' }}>
                                    <Power size={48} style={{ marginBottom: '1rem', color: '#ef4444', marginLeft: 'auto', marginRight: 'auto', display: 'block' }} />
                                    <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--etpos-text)' }}>No Active Connection</h4>
                                    <p style={{ fontSize: '0.875rem', margin: '0 0 1rem 0' }}>Connect your QuickBooks Sandbox account to start syncing transactions.</p>
                                    <button
                                        onClick={handleConnectQb}
                                        disabled={qbLoading}
                                        style={{ background: '#3b82f6', color: 'white', padding: '0.65rem 1.5rem', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                                    >
                                        Connect QuickBooks
                                    </button>
                                </div>
                            )}

                            {/* Action Buttons */}
                            {isConnected && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
                                    <button onClick={handleTestConnection} disabled={qbLoading} style={qbBtnStyle('#06b6d4')}>
                                        <CheckCircle size={16} /> Test Connection
                                    </button>
                                    <button onClick={handleRefreshStatus} disabled={qbLoading} style={qbBtnStyle('#fb923c')}>
                                        <RefreshCw size={16} /> Refresh Status
                                    </button>
                                    <button onClick={handleDisconnect} disabled={qbLoading} style={qbBtnStyle('#f43f5e')}>
                                        <Power size={16} /> Disconnect
                                    </button>
                                </div>
                            )}

                            {/* API Test Result Panel */}
                            {qbApiTestResult && (
                                <div style={{
                                    marginBottom: '1.5rem', padding: '1rem 1.25rem', borderRadius: '6px',
                                    background: qbApiTestResult.type === 'success' ? '#f0fdf4' : qbApiTestResult.type === 'error' ? '#fef2f2' : '#eff6ff',
                                    border: `1px solid ${qbApiTestResult.type === 'success' ? '#86efac' : qbApiTestResult.type === 'error' ? '#fca5a5' : '#93c5fd'}`,
                                    color: qbApiTestResult.type === 'success' ? '#166534' : qbApiTestResult.type === 'error' ? '#991b1b' : '#1e40af',
                                    fontSize: '0.875rem'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1 }}>
                                            <strong>{qbApiTestResult.label}:</strong>{' '}
                                            {qbApiTestResult.type === 'loading' && 'Loading...'}
                                            {qbApiTestResult.message && qbApiTestResult.message}
                                            {qbApiTestResult.data && (
                                                <pre style={{ marginTop: '0.5rem', fontSize: '0.75rem', background: 'rgba(0,0,0,0.05)', padding: '0.5rem', borderRadius: '4px', overflow: 'auto', maxHeight: '120px' }}>
                                                    {JSON.stringify(qbApiTestResult.data, null, 2)}
                                                </pre>
                                            )}
                                        </div>
                                        <button onClick={() => setQbApiTestResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, marginLeft: '1rem', color: 'inherit', opacity: 0.6 }}>×</button>
                                    </div>
                                </div>
                            )}

                            {/* Quick API Tests */}
                            {isConnected && (
                                <div style={{ marginBottom: '2.5rem' }}>
                                    <h5 style={{ margin: '0 0 1rem 0', color: 'var(--etpos-text)', fontSize: '0.95rem', fontWeight: '600' }}>⚡ Quick API Tests</h5>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center' }}>
                                        {[
                                            { label: 'Accounts',  icon: Server,   fn: () => quickbooksApi.getAccounts() },
                                            { label: 'Customers', icon: User,     fn: () => quickbooksApi.getCustomers() },
                                            { label: 'Vendors',   icon: Store,    fn: () => quickbooksApi.getVendors() },
                                            { label: 'Invoices',  icon: FileText, fn: () => quickbooksApi.getInvoices() },
                                            { label: 'Items',     icon: Tag,      fn: () => quickbooksApi.getItems() },
                                        ].map((test, index) => {
                                            const Icon = test.icon;
                                            return (
                                                <button
                                                    key={index}
                                                    onClick={() => handleQuickApiTest(test.label, test.fn)}
                                                    style={{ background: 'transparent', border: 'none', color: 'var(--etpos-text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', fontWeight: '500', padding: '0.25rem 0.5rem', borderRadius: '4px', transition: 'background 0.2s' }}
                                                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--etpos-bg)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                >
                                                    <Icon size={16} style={{ color: '#3b82f6' }} />
                                                    <span>{test.label}</span>
                                                </button>
                                            );
                                        })}
                                        <button
                                            onClick={() => setQbApiTestResult(null)}
                                            style={{ background: 'transparent', border: 'none', color: 'var(--etpos-text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', fontWeight: '500', padding: '0.25rem 0.5rem', borderRadius: '4px', transition: 'background 0.2s' }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--etpos-bg)'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <Trash2 size={16} style={{ color: '#3b82f6' }} />
                                            <span>Clear</span>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Account Mapping */}
                            {isConnected && (
                                <div style={{ borderTop: '1px solid var(--etpos-border)', paddingTop: '2rem', marginBottom: '2.5rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                        <h4 style={{ margin: 0, color: 'var(--etpos-text)', fontSize: '1.1rem', fontWeight: '600' }}># Account Mapping</h4>
                                        <button
                                            onClick={fetchQbAccounts}
                                            disabled={qbAccountsLoading}
                                            style={{ background: 'transparent', border: 'none', color: '#3b82f6', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem', fontWeight: '600' }}
                                        >
                                            <RefreshCw size={14} style={{ animation: qbAccountsLoading ? 'spin 1s linear infinite' : 'none' }} />
                                            {qbAccountsLoading ? 'Loading...' : 'Refresh Accounts'}
                                        </button>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem 2rem', marginBottom: '1.5rem' }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>
                                                Income Account (Sales) <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <select value={qbIncomeAccountId} onChange={(e) => setQbIncomeAccountId(e.target.value)} style={qbSelectStyle} disabled={qbAccountsLoading}>
                                                <option value="">-- Select Account --</option>
                                                {qbAccountOptions}
                                            </select>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted)', marginTop: '0.3rem' }}>Select the account for recording sales income.</div>
                                        </div>

                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>
                                                Expense Account (Purchases)
                                            </label>
                                            <select value={qbExpenseAccountId} onChange={(e) => setQbExpenseAccountId(e.target.value)} style={qbSelectStyle} disabled={qbAccountsLoading}>
                                                <option value="">-- Select Account --</option>
                                                {qbAccountOptions}
                                            </select>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted)', marginTop: '0.3rem' }}>Select the account for recording purchase expenses.</div>
                                        </div>

                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>
                                                Asset Account (Inventory)
                                            </label>
                                            <select value={qbAssetAccountId} onChange={(e) => setQbAssetAccountId(e.target.value)} style={qbSelectStyle} disabled={qbAccountsLoading}>
                                                <option value="">-- Select Account --</option>
                                                {qbAccountOptions}
                                            </select>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted)', marginTop: '0.3rem' }}>Select the account for inventory assets.</div>
                                        </div>

                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--etpos-text)' }}>
                                                Deposit Account (Bank/Cash)
                                            </label>
                                            <select value={qbDepositAccountId} onChange={(e) => setQbDepositAccountId(e.target.value)} style={qbSelectStyle} disabled={qbAccountsLoading}>
                                                <option value="">-- Select Account --</option>
                                                {qbAccountOptions}
                                            </select>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted)', marginTop: '0.3rem' }}>Select the account where payments are deposited.</div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
                                        <input
                                            type="checkbox"
                                            id="qb-auto-sync"
                                            checked={qbAutoSync}
                                            onChange={(e) => setQbAutoSync(e.target.checked)}
                                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                        />
                                        <label htmlFor="qb-auto-sync" style={{ fontSize: '0.9rem', color: 'var(--etpos-text)', fontWeight: '600', cursor: 'pointer' }}>
                                            Automatically sync transactions when created
                                        </label>
                                    </div>

                                    <button
                                        onClick={handleSaveMappings}
                                        disabled={qbSavingSettings}
                                        style={{ background: '#3b82f6', color: 'white', padding: '0.65rem 1.25rem', border: 'none', borderRadius: '6px', fontWeight: '600', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: qbSavingSettings ? 'not-allowed' : 'pointer', opacity: qbSavingSettings ? 0.7 : 1, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                                    >
                                        <Save size={16} /> {qbSavingSettings ? 'Saving...' : 'Save Mappings'}
                                    </button>
                                </div>
                            )}

                            {/* Manual Sync Actions */}
                            {isConnected && (
                                <div style={{ borderTop: '1px solid var(--etpos-border)', paddingTop: '2rem' }}>
                                    <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--etpos-text)', fontSize: '1.1rem', fontWeight: '600' }}>📝 Manual Sync Actions</h4>
                                    <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.85rem', color: 'var(--etpos-text-muted)' }}>
                                        Use these buttons to manually push data to QuickBooks. Useful for initial setup or if auto-sync fails.
                                    </p>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                                        <button onClick={() => handleManualSync('Customers')} disabled={qbSyncStatus.loading} style={qbBtnStyle('#0ea5e9')}>
                                            <Users size={16} /> {qbSyncStatus.loading ? 'Syncing...' : 'Sync Customers'}
                                        </button>
                                        <button onClick={() => handleManualSync('Products')} disabled={qbSyncStatus.loading} style={qbBtnStyle('#fb923c')}>
                                            <Package size={16} /> {qbSyncStatus.loading ? 'Syncing...' : 'Sync Products'}
                                        </button>
                                        <button onClick={() => handleManualSync('Invoices')} disabled={qbSyncStatus.loading} style={qbBtnStyle('#10b981')}>
                                            <FileText size={16} /> {qbSyncStatus.loading ? 'Syncing...' : 'Sync Invoices'}
                                        </button>
                                    </div>

                                    {/* Sync Status Result */}
                                    {(qbSyncStatus.message || qbSyncStatus.loading) && (
                                        <div style={{ marginTop: '1rem', padding: '0.875rem 1rem', borderRadius: '6px', background: qbSyncStatus.errors?.length ? '#fef2f2' : '#f0fdf4', border: `1px solid ${qbSyncStatus.errors?.length ? '#fca5a5' : '#86efac'}`, color: qbSyncStatus.errors?.length ? '#991b1b' : '#166534', fontSize: '0.875rem' }}>
                                            {qbSyncStatus.loading ? '⏳ Syncing in progress...' : `✓ ${qbSyncStatus.message}`}
                                            {qbSyncStatus.errors?.length > 0 && (
                                                <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem' }}>
                                                    {qbSyncStatus.errors.map((err, i) => <li key={i}>{err}</li>)}
                                                </ul>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>
                    </div>
                );
            default:
                return (
                    <div style={{ color: 'var(--etpos-text-muted)', fontSize: '0.9rem', textAlign: 'center', marginTop: '4rem' }}>
                        <p>Form content for {TABS.find(t => t.id === activeTab)?.label} will go here.</p>
                        <p>Ready to add inputs when requested.</p>
                    </div>
                );
        }
    };

    return (
        <div className="settings-layout">

            {/* ── Left Sidebar (Vertical Tabs) ── */}
            <div className="settings-sidebar">
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`settings-tab-btn ${activeTab === tab.id ? 'active' : 'inactive'}`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ── Form Content Area ── */}
            <div className="settings-main-area">
                <div className="settings-content-card">
                    {/* Heading removed per user request */}

                    {renderTabContent()}
                </div>

                {/* Global Update Settings Button */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem', marginBottom: '2rem' }}>
                    <button
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.75rem 2.5rem',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        onClick={() => {
                            if (activeTab === 'quickbooks') {
                                handleSaveMappings();
                            } else {
                                alert('Updating ' + activeTab + ' settings...');
                            }
                        }}
                    >
                        Update Settings
                    </button>
                </div>
            </div>

        </div>
    );
};

export default BusinessSettings;

